'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';
import { Icon } from './icons';

const dialogCascade =
  '[&>*:not([data-slot=dialog-close])]:animate-in ' +
  '[&>*:not([data-slot=dialog-close])]:fade-in ' +
  '[&>*:not([data-slot=dialog-close])]:slide-in-from-left-8 ' +
  '[&>*:not([data-slot=dialog-close])]:duration-500 ' +
  '[&>*:not([data-slot=dialog-close])]:ease-out ' +
  '[&>*:not([data-slot=dialog-close])]:[--tw-animation-fill-mode:backwards] ' +
  '[&>*]:[--tw-animation-delay:0ms] ' +
  '[&>*:nth-child(2)]:[--tw-animation-delay:100ms] ' +
  '[&>*:nth-child(3)]:[--tw-animation-delay:200ms] ' +
  '[&>*:nth-child(4)]:[--tw-animation-delay:300ms] ' +
  '[&>*:nth-child(5)]:[--tw-animation-delay:400ms] ' +
  '[&>*:nth-child(n+6)]:[--tw-animation-delay:500ms]';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" className="cursor-pointer" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        `data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in
        data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 duration-100`,
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  cascade = true,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  cascade?: boolean;
  /** Extra classes for the backdrop — e.g. to fade it with the panel. */
  overlayClassName?: string;
}) {
  const tCommon = useTranslations('common');
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          `bg-background data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=open]:animate-in data-[state=open]:fade-in-0
          max-sm:data-[state=open]:slide-in-from-bottom-4 max-sm:data-[state=closed]:slide-out-to-bottom-4
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          fixed left-[50%] top-[50%] z-50 grid max-w-[calc(100%-2rem)]
          translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg`,
          cascade && dialogCascade,
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="rounded-xs ring-offset-background focus:ring-ring focus:outline-hidden data-[state=open]:bg-accent
              data-[state=open]:text-muted-foreground absolute right-4 top-4 opacity-70 transition-opacity
              hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none
              [&_svg:not([class*='size-'])]:size-6 [&_svg]:pointer-events-none [&_svg]:shrink-0"
          >
            <Icon name="CloseCircle" size={24} />
            <span className="sr-only">{tCommon('close')}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('pr-10 text-lg font-semibold leading-none', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
